import { StatusCodes } from 'http-status-codes';

import Notification from '../../components/Notification/Notification';
import responseCodes from '../../constants/responseCodes';
import i18n, { initializeUserLocale } from '../../i18n';
import baseServerUrl from '../../services/apiServer';
import * as authService from '../../services/authService';
import * as userService from '../../services/userService';
import authActionConstants from '../constants/authActionConstants';
import globalActionConstants from '../constants/globalActionConstants';
import stepsActionsConstants from '../constants/stepsActionsConstants';

const t = (key) => i18n.t(key, { ns: 'common' });

export const getCurrentUser = () => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  dispatch({ type: authActionConstants.GET_CURRENT_USER_LOADING_ON });

  try {
    const response = await authService.getCurrentUser();
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: authActionConstants.GET_CURRENT_USER, payload: response.data.payload });
      dispatch({ type: authActionConstants.GET_CURRENT_USER_LOADING_OFF });
      // Initialize user's preferred language from their saved preference
      if (response.data.payload?.user?.locale) {
        initializeUserLocale(response.data.payload.user.locale);
      }
    } else {
      Notification.error(response.data.error);
      dispatch({ type: authActionConstants.GET_CURRENT_USER_LOADING_OFF });
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    dispatch({ type: authActionConstants.GET_CURRENT_USER_LOADING_OFF });
    if (err.response && err.response.status !== StatusCodes.UNAUTHORIZED) {
      Notification.error(t('notifications.somethingWentWrong'));
    }
  }
};

export const signOut = () => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    window.location.href = `${baseServerUrl}/logout`;
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const validatePassword = (userId, password, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await authService.validatePassword(userId, password);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });

    if (response.data.response_code === responseCodes.OK) {
      if (successCallback) {
        successCallback();
      }
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const getNumberConfirmation = (formData, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  dispatch({ type: authActionConstants.CONFIRM_NUMBER });

  try {
    const res = await authService.getNumberConfirmation(formData);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });

    if (res.data.response_code === responseCodes.OK) {
      dispatch({
        type: authActionConstants.GET_NUMBER_CONFIRMATION_SUCCESS,
        payload: {
          formData,
          attempts: res.data.payload.verification.attempts,
        },
      });
      Notification.success(t('notifications.confirmationCodeSent'));
      if (successCallback) {
        successCallback();
      }
    } else {
      Notification.error(res.data.error || t('notifications.failedToSendConfirmationCode'));
    }
  } catch (error) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const confirmNumber = (formData, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  dispatch({ type: authActionConstants.CONFIRM_NUMBER });

  const confirmData = {
    confirmation_code: formData.confirmation_code,
    phone_number: formData.phone_number,
  };

  return authService.confirmPhone(confirmData).then((response) => {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: authActionConstants.CONFIRM_NUMBER_SUCCESS });
      dispatch({
        type: stepsActionsConstants.GET_STEP_SUCCESS,
        payload: response.data.payload.current_incomplete_step,
      });
      Notification.success(t('notifications.phoneConfirmed'));
      if (successCallback) {
        successCallback();
      }
    } else {
      Notification.error(response.data.error);
      dispatch({ type: authActionConstants.CONFIRM_NUMBER_ERROR });
    }
  });
};

export function sendSkipMobileVerification() {
  return (dispatch) => {
    dispatch({ type: globalActionConstants.SET_LOADING_ON });
    authService.skipMobileVerification().then((response) => {
      if (response.data.response_code === responseCodes.OK) {
        dispatch({ type: globalActionConstants.SET_LOADING_OFF });
        dispatch({
          type: stepsActionsConstants.GET_STEP_SUCCESS,
          payload: response.data.payload.current_incomplete_step,
        });
        Notification.success(t('notifications.mobileVerificationSkipped'));
      } else {
        Notification.error(response.data.error);
      }
    });
  };
}

export function updateUserLocation(formValues, successCallback) {
  return (dispatch) => {
    dispatch({ type: globalActionConstants.SET_LOADING_ON });
    return authService
      .updateUserLocation(formValues)
      .then((response) => {
        dispatch({
          type: stepsActionsConstants.GET_STEP_SUCCESS,
          payload: response.data.payload.current_incomplete_step,
        });
        dispatch({ type: globalActionConstants.SET_LOADING_OFF });
        if (successCallback) {
          successCallback();
        }
      })
      .catch((response) => {
        dispatch({ type: globalActionConstants.SET_LOADING_OFF });
        Notification.error(response.data.error);
      });
  };
}

export const createOrganizationAccessRequest = (requestData, successCallback) => async (dispatch) => {
  dispatch({ type: authActionConstants.CREATE_ORGANIZATION_ACCESS_REQUEST });

  try {
    const response = await userService.createOrganizationAccessRequest(requestData);
    if (response.data.response_code === responseCodes.OK) {
      dispatch({
        type: authActionConstants.CREATE_ORGANIZATION_ACCESS_REQUEST_SUCCESS,
        payload: response.data.payload,
      });
      Notification.success(t('notifications.accessRequestSubmitted'));
      if (successCallback) successCallback();
    } else {
      dispatch({
        type: authActionConstants.CREATE_ORGANIZATION_ACCESS_REQUEST_ERROR,
        payload: response.data.error,
      });
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({
      type: authActionConstants.CREATE_ORGANIZATION_ACCESS_REQUEST_ERROR,
      payload: 'Something went wrong',
    });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export function resendConfirmationCode(formData) {
  return (dispatch) => {
    dispatch({ type: authActionConstants.RESEND_CONFIRMATION_CODE });
    authService.resendConfirmationCode(formData).then((res) => {
      if (res.data.response_code === responseCodes.OK) {
        dispatch({
          type: authActionConstants.RESEND_CONFIRMATION_CODE_SUCCESS,
          payload: { formData, attempts: res.data.payload.verification.attempts },
        });
        Notification.success(t('notifications.confirmationCodeSent'));
      } else {
        dispatch({ type: authActionConstants.RESEND_CONFIRMATION_CODE_ERROR, payload: res.data.error });
        Notification.error(res.data.error);
      }
    });
  };
}
